import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VissmedApi, type VissmedImportResponse, type VissmedLastImport } from '../lib/crmApi';
import { humanizeCrmError } from '../lib/apiErrors';
import { useToast } from '../components/Toast';

/** Formata centavos em R$ brasileiro. Null → "—". */
function fmtMoney(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtInt(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString('pt-BR');
}

/**
 * Valor médio por atendimento = gasto total / número de atendimentos.
 * Pra pacientes com 1 só atendimento, isso é exatamente o valor da última visita.
 * Pra quem tem vários, é a média (Total de Consultas (R$) / Total de Consultas do Vissmed).
 */
function ticketMedio(totalCents: number | null | undefined, totalVisits: number | null | undefined): number | null {
  if (totalCents == null || !totalVisits || totalVisits <= 0) return null;
  return Math.round(totalCents / totalVisits);
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('pt-BR');
}

export default function AdminVissmedImport() {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState<VissmedImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [lastImports, setLastImports] = useState<VissmedLastImport[]>([]);
  const [lastImportsError, setLastImportsError] = useState<string | null>(null);

  useEffect(() => {
    void reloadLastImports();
  }, []);

  const reloadLastImports = useCallback(async () => {
    setLastImportsError(null);
    const res = await VissmedApi.lastImports();
    if (res.ok && res.data) {
      setLastImports(res.data.imports);
      return;
    }
    const msg = humanizeCrmError(res.error, res.status);
    setLastImportsError(msg);
  }, []);

  function onSelectFile(f: File | null) {
    setFile(f);
    setResult(null);
    setError(null);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f) onSelectFile(f);
  }

  async function analyze() {
    if (!file) return;
    setAnalyzing(true);
    setError(null);
    setResult(null);
    const res = await VissmedApi.preview(file);
    setAnalyzing(false);
    if (!res.ok) {
      const msg = humanizeCrmError(res.error, res.status);
      setError(msg);
      toast.error(msg);
      return;
    }
    setResult(res.data ?? null);
  }

  async function commit() {
    if (!file) return;
    if (!confirm(`Confirma importar ${result?.stats.validRows ?? '?'} pacientes no CRM?`)) return;
    setCommitting(true);
    const res = await VissmedApi.commit(file);
    setCommitting(false);
    if (!res.ok) {
      const msg = humanizeCrmError(res.error, res.status);
      setError(msg);
      toast.error(msg);
      return;
    }
    setError(null);
    setResult(res.data ?? null);
    toast.success(
      `Import concluído: ${res.data?.result?.created ?? 0} criados, ${res.data?.result?.updated ?? 0} atualizados`,
      7000,
    );
    void reloadLastImports();
  }

  const showPreview = !!result;
  const isDryRun = result?.mode === 'dry_run';
  const unmappedColumns = useMemo(() => {
    if (!result?.mapping) return [];
    return Object.entries(result.mapping)
      .filter(([, v]) => v == null)
      .map(([k]) => k);
  }, [result]);

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Importar pacientes Vissmed</h1>
        <p className="text-sm text-slate-600 mt-1">
          Sobe o XLS gerado em{' '}
          <span className="font-medium">Gerência Operacional → Relação de Clientes</span> do Vissmed.
          Extrai nome, telefone, CPF, LTV, última consulta e origem. Dedup por <code>ID do Paciente</code>.
        </p>
      </div>

      {/* Dropzone / upload */}
      <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={onDrop}
        className="border-2 border-dashed border-slate-300 hover:border-blue-400 bg-white rounded-lg p-8 text-center cursor-pointer transition"
        onClick={() => fileRef.current?.click()}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".xls,.xlsx,.csv"
          className="hidden"
          onChange={(e) => onSelectFile(e.target.files?.[0] ?? null)}
        />
        {!file ? (
          <>
            <div className="text-slate-700 font-medium">Arraste o arquivo aqui ou clique para selecionar</div>
            <div className="text-xs text-slate-500 mt-1">Aceita .xls, .xlsx e .csv — até 30MB</div>
          </>
        ) : (
          <>
            <div className="text-slate-900 font-medium">{file.name}</div>
            <div className="text-xs text-slate-500 mt-1">
              {(file.size / 1024).toFixed(1)} KB — {new Date(file.lastModified).toLocaleString('pt-BR')}
            </div>
            <button
              className="mt-3 text-xs text-red-600 hover:underline"
              onClick={(e) => { e.stopPropagation(); onSelectFile(null); }}
            >
              remover
            </button>
          </>
        )}
      </div>

      {file && (
        <div className="flex gap-3">
          <button
            onClick={analyze}
            disabled={analyzing || committing}
            className="px-4 py-2 rounded bg-slate-800 text-white font-medium hover:bg-slate-700 disabled:opacity-50"
          >
            {analyzing ? 'Analisando…' : 'Analisar arquivo (dry-run)'}
          </button>
          {showPreview && isDryRun && (
            <button
              onClick={commit}
              disabled={committing || (result?.stats.validRows ?? 0) === 0}
              className="px-4 py-2 rounded bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {committing ? 'Importando…' : `Confirmar import de ${result?.stats.validRows ?? 0} pacientes`}
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm">
          {error}
        </div>
      )}

      {/* Resultado */}
      {showPreview && result && (
        <div className="space-y-6">
          {/* Status bar */}
          <div
            className={`rounded-lg p-4 ${
              result.mode === 'commit'
                ? 'bg-green-50 border border-green-200'
                : 'bg-blue-50 border border-blue-200'
            }`}
          >
            <div className="font-semibold text-slate-900">
              {result.mode === 'commit'
                ? '✔ Import concluído'
                : 'Prévia (nada foi gravado ainda)'}
            </div>
            {result.mode === 'commit' && result.result && (
              <div className="text-sm text-slate-700 mt-1">
                {result.result.created} novos pacientes criados · {result.result.updated} atualizados ·{' '}
                {result.result.snapshots} snapshots Vissmed · {result.result.elapsedMs} ms
              </div>
            )}
          </div>

          {/* Stats cards — só o que importa: pacientes, dinheiro total, contato disponível */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Pacientes na planilha" value={fmtInt(result.stats.totalRead)} />
            <StatCard label="Pacientes processados" value={fmtInt(result.stats.validRows)} highlight />
            <StatCard label="Faturamento total" value={fmtMoney(result.stats.totalLtvCents)} highlight />
            <StatCard label="Com telefone" value={fmtInt(result.stats.withPhone)} />
          </div>

          {/* Mapping */}
          <section>
            <h2 className="font-semibold text-slate-900 mb-2">Mapeamento de colunas detectado</h2>
            <div className="bg-white border border-slate-200 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium text-slate-600">Campo CRM</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Coluna Vissmed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {Object.entries(result.mapping).map(([field, col]) => (
                    <tr key={field}>
                      <td className="px-3 py-1.5 font-mono text-xs text-slate-700">{field}</td>
                      <td className="px-3 py-1.5 text-slate-800">
                        {col ?? <span className="text-slate-400 italic">— não encontrada —</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {unmappedColumns.length > 0 && (
              <div className="text-xs text-amber-700 mt-2">
                Colunas opcionais não mapeadas: {unmappedColumns.join(', ')}. Não bloqueia import —
                rows serão gravadas com esses campos como null.
              </div>
            )}
          </section>

          {/* Top por TICKET MÉDIO (valor por atendimento) — métrica que faz sentido pra clínica */}
          <section>
            <h2 className="font-semibold text-slate-900 mb-1">Top 10 pacientes por ticket médio</h2>
            <p className="text-xs text-slate-500 mb-2">
              Ticket médio = LTV total ÷ qtd de atendimentos. Pra pacientes com 1 atendimento, é o
              valor exato da última visita.
            </p>
            <div className="bg-white border border-slate-200 rounded overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium text-slate-600">#</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Nome</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Última especialidade</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-right">Ticket médio</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-right">LTV total</th>
                    <th className="px-3 py-2 font-medium text-slate-600">1ª consulta</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Última visita</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[...result.preview.topLtv]
                    .sort(
                      (a, b) =>
                        (ticketMedio(b.total_spent_cents, b.total_visits) ?? 0) -
                        (ticketMedio(a.total_spent_cents, a.total_visits) ?? 0),
                    )
                    .slice(0, 10)
                    .map((r, i) => (
                      <tr key={r.vissmed_id ?? i} className="hover:bg-slate-50">
                        <td className="px-3 py-1.5 text-slate-500">{i + 1}</td>
                        <td className="px-3 py-1.5 font-medium text-slate-900">{r.full_name}</td>
                        <td className="px-3 py-1.5 text-slate-700 text-xs">
                          {r.last_specialty ?? <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-1.5 text-right text-green-700 font-semibold tabular-nums">
                          {fmtMoney(ticketMedio(r.total_spent_cents, r.total_visits))}
                        </td>
                        <td className="px-3 py-1.5 text-right text-slate-700 tabular-nums">
                          {fmtMoney(r.total_spent_cents)}
                        </td>
                        <td className="px-3 py-1.5 text-slate-700">{fmtDate(r.first_visit_at)}</td>
                        <td className="px-3 py-1.5 text-slate-700">{fmtDate(r.last_visit_at)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Amostra 10 primeiras linhas */}
          <section>
            <h2 className="font-semibold text-slate-900 mb-2">Amostra — 10 primeiras linhas</h2>
            <div className="bg-white border border-slate-200 rounded overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium text-slate-600">Nome</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Telefone</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Última especialidade</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-right">Ticket médio</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-right">LTV total</th>
                    <th className="px-3 py-2 font-medium text-slate-600">1ª consulta</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Última visita</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {result.preview.firstRows.map((r, i) => (
                    <tr key={r.vissmed_id ?? i}>
                      <td className="px-3 py-1.5 text-slate-900">{r.full_name}</td>
                      <td className="px-3 py-1.5 font-mono text-xs text-slate-700">
                        {r.phone_e164 ?? <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-1.5 text-slate-700 text-xs">
                        {r.last_specialty ?? <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {fmtMoney(ticketMedio(r.total_spent_cents, r.total_visits))}
                      </td>
                      <td className="px-3 py-1.5 text-right text-slate-700 tabular-nums">
                        {fmtMoney(r.total_spent_cents)}
                      </td>
                      <td className="px-3 py-1.5 text-slate-700">{fmtDate(r.first_visit_at)}</td>
                      <td className="px-3 py-1.5 text-slate-700">{fmtDate(r.last_visit_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Issues */}
          {result.preview.issues.length > 0 && (
            <section>
              <h2 className="font-semibold text-slate-900 mb-2">
                Warnings ({result.stats.issues} no total · mostrando primeiros 20)
              </h2>
              <div className="bg-amber-50 border border-amber-200 rounded max-h-64 overflow-y-auto text-xs font-mono">
                {result.preview.issues.map((iss, i) => (
                  <div key={i} className="px-3 py-1 border-b border-amber-100 last:border-b-0">
                    <span className="text-slate-500">L{iss.line}:</span> {iss.reason}
                  </div>
                ))}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Warnings não bloqueiam import — rows continuam sendo gravadas quando há telefone OU CPF válido.
              </div>
            </section>
          )}
        </div>
      )}

      {/* Histórico de imports */}
      <section className="pt-6 border-t border-slate-200">
        <h2 className="font-semibold text-slate-900 mb-2">Últimos imports</h2>
        {lastImportsError && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 text-sm mb-3 flex flex-wrap items-center justify-between gap-2">
            <span>{lastImportsError}</span>
            <button
              type="button"
              onClick={() => void reloadLastImports()}
              className="text-sm font-medium text-amber-800 underline hover:no-underline"
            >
              Tentar de novo
            </button>
          </div>
        )}
        {!lastImportsError && lastImports.length === 0 && (
          <p className="text-sm text-slate-500">Nenhum import registrado ainda.</p>
        )}
        {lastImports.length > 0 && (
          <div className="bg-white border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium text-slate-600">Data</th>
                  <th className="px-3 py-2 font-medium text-slate-600">Tipo</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-right">Criados</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-right">Atualizados</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-right">Snapshots</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lastImports.map((imp) => (
                  <tr key={imp.id}>
                    <td className="px-3 py-1.5 text-slate-700">
                      {new Date(imp.sync_date).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs text-slate-600">{imp.sync_type}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {fmtInt(imp.by_type?.created)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {fmtInt(imp.by_type?.updated)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {fmtInt(imp.by_type?.snapshots)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({
  label,
  value,
  highlight,
  muted,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      className={`rounded border p-3 ${
        highlight
          ? 'bg-blue-50 border-blue-200'
          : muted
            ? 'bg-slate-50 border-slate-200'
            : 'bg-white border-slate-200'
      }`}
    >
      <div className="text-xs text-slate-600">{label}</div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${highlight ? 'text-blue-800' : 'text-slate-900'}`}>
        {value}
      </div>
    </div>
  );
}
